import React, { useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';
import Toolbar from '@mui/material/Toolbar';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { Box, Grid } from '@mui/material';
import BootstrapDateRangePicker from '../../date/BootstrapDateRangePicker';
import moment from 'moment/moment';
import getRequest from '../../../assets/helper/ApiHandler';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { formatNumberWithCommas } from '../../template/PrefixAndSuffixBody';
import BeatLoading from '../../beatloader';




export default function DashboardPage() {

    const [chartData, setChartData] = useState({});
    const [chartIncomingData, setChartIncomingData] = useState([]);
    const [chartOptions, setChartOptions] = useState({});
    const [metrics, setMetrics] =useState({});
    const [loading, setLoading] =useState();
    const [dates, setDates] = useState({
        startDate: moment().startOf('week').format('D MMM, YYYY'),
        endDate: moment().format('D MMM, YYYY')
    });
    const navigate = useNavigate();
    const toast = useRef(null);


    useEffect(() => {
        fetchData(dates.startDate, dates.endDate);
        fetchCharts(dates.startDate, dates.endDate);
    }, [dates]);

    const fetchData = async (startDate, endDate) => {
        setLoading(true);
        const sd = moment(startDate).format('YYYY-MM-DD');
        const ed = moment(endDate).format('YYYY-MM-DD');
        try {
            const dashboardResponse = await getRequest(`/report/dashboard?startDate=${encodeURIComponent(sd)}&endDate=${encodeURIComponent(ed)}`, null, navigate);
            setMetrics(dashboardResponse[0])
        }
        catch (error) { 
            if(toast.current){
                toast.current.show({ severity: "error", summary: 'Error', detail: error });
            }
        }
        finally{
            setLoading(false)
        }
    };

    const fetchCharts = async (startDate, endDate)=> {
        setLoading(true);
        const sd = moment(startDate).format('YYYY-MM-DD');
        const ed = moment(endDate).format('YYYY-MM-DD');
        try {
            const chartResponse = await getRequest(`/report/dashboard/chart?startDate=${encodeURIComponent(sd)}&endDate=${encodeURIComponent(ed)}`, null, navigate);
            setChartIncomingData(chartResponse);
        }
        catch (error) {
            if(toast.current){
                toast.current.show({ severity: "error", summary: 'Error', detail: error });
            }
        } 
        finally{
            setLoading(false);
        }
    };

    
    useEffect(() => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color');
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border');
        const label = chartIncomingData.map(item => item.date);
        const totalRevenueData = chartIncomingData.map(item => item.totalRevenue);
        const mediaCost = chartIncomingData.map(item=> item.mediaCost);
        const omegaCost = chartIncomingData.map(item=> item.omegaCost);
        const netIncome = chartIncomingData.map(item=> item.netIncome);

        const data = {
            labels: label,
            datasets: [
                {
                    label: 'Total Revenue',
                    data: totalRevenueData,
                    fill: false,
                    tension: 0.4,
                    borderWidth: 2,
                    borderColor: "#3498DB",
                    backgroundColor: "#3498DB",
                    pointBackgroundColor: "#ffffff",
                    pointBorderColor: "#3498DB",
                    pointHoverBackgroundColor: "#ffffff",
                    pointHoverBorderColor: "#3498DB",
                    hoverBorderWidth: 4,
                    pointStyle: 'circle',
                    pointRadius: 4,
                    pointHoverRadius: 7,
                },
                {
                    label: 'Media Cost',
                    data: mediaCost,
                    fill: false,
                    borderColor: documentStyle.getPropertyValue('--pink-500'),
                    tension: 0.4,
                    borderWidth: 2,
                    borderColor: "#E74C3C",
                    backgroundColor: "#E74C3C",
                    pointBackgroundColor: "#ffffff",
                    pointBorderColor: "#E74C3C",
                    pointHoverBackgroundColor: "#ffffff",
                    pointHoverBorderColor: "#E74C3C",
                    hoverBorderWidth: 4,
                    pointStyle: 'circle',
                    pointRadius: 4,
                    pointHoverRadius: 7,
                },
                {
                    label: 'Omega Cost',
                    data: omegaCost,
                    fill: false,
                    borderColor: documentStyle.getPropertyValue('--pink-500'),
                    tension: 0.4,
                    borderWidth: 2,
                    borderColor: "#F1C40F",
                    backgroundColor: "#F1C40F",
                    pointBackgroundColor: "#ffffff",
                    pointBorderColor: "#F1C40F",
                    pointHoverBackgroundColor: "#ffffff",
                    pointHoverBorderColor: "#F1C40F",
                    hoverBorderWidth: 4,
                    pointStyle: 'circle',
                    pointRadius: 4,
                    pointHoverRadius: 7,
                },
                {
                    label: 'Net Income',
                    data: netIncome,
                    fill: false,
                    borderColor: documentStyle.getPropertyValue('--pink-500'),
                    tension: 0.4,
                    borderWidth: 2,
                    borderColor: "#17B26A",
                    backgroundColor: "#17B26A",
                    pointBackgroundColor: "#ffffff",
                    pointBorderColor: "#17B26A",
                    pointHoverBackgroundColor: "#ffffff",
                    pointHoverBorderColor: "#17B26A",
                    hoverBorderWidth: 4,
                    pointStyle: 'circle',
                    pointRadius: 4,
                    pointHoverRadius: 7,
                }                
            ]
        };
        const options = {
            maintainAspectRatio: false,
            aspectRatio: 0.6,
            lineTension: 0,
            type: 'line',
            plugins: {
                legend: {
                    display: false,
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary
                    },
                    grid: {
                        display: false,
                        color: surfaceBorder,
                        drawBorder: false
                    }
                },
                y: {
                    ticks: {
                        color: textColorSecondary
                    },
                    grid: {
                        borderDash: [2, 2],
                        color: surfaceBorder
                    }
                }
            }
        };

       setChartData(data);
        setChartOptions(options);
    }, [chartIncomingData]);


    const handleSelectedDate = (selectedDates) => {
        const formattedStartDate = moment(selectedDates.startDate).format('D MMM, YYYY');
        const formattedEndDate = moment(selectedDates.endDate).format('D MMM, YYYY');
        setDates({
            startDate: formattedStartDate,
            endDate: formattedEndDate
        });
    }

    return (
        <div className="card">
            <Toolbar className='main-content-header'>
                <Typography variant="h6" noWrap component="div">
                    <h2>Dashboard</h2>
                </Typography>
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    sx={{ mr: 2, display: { sm: 'none' } }}>
                    <MenuIcon />
                </IconButton>
            </Toolbar>
            <Box sx={{ padding: '1rem 0', borderRadius: '5px' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={12} className='chart-main'>
                        <div className="flex justify-content-end" style={{ display: 'flex', justifyContent: 'end' }}>
                            <span className="p-input-icon-left">
                                <i className="pi pi-calendar" />
                                <BootstrapDateRangePicker
                                    dates={dates}
                                    selectedDate={handleSelectedDate}
                                />
                            </span>
                        </div>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ padding: '1rem', bgcolor: '#F9FAFB', borderRadius: '5px' }}>
                <Grid container spacing={2}>
                
                    <Grid item xs={9} md={9} className='chart-main'>
                        <h2 className='fs-18'>Performance</h2>
                        <p className='fs-3'>{dates.startDate} to {dates.endDate}</p>
                        <br></br>
                        <div> { loading ? (
                <BeatLoading />
            ) : (   
                        <Chart type="line" data={chartData} options={chartOptions} />
                        )}
                    </div>
                    </Grid>
                    <Grid item xs={3} md={3} className='chart-summary'>
                        <h2 className='fs-18'>Core Metrics</h2>
                        <p className='fs-3'>{dates.startDate} to {dates.endDate}</p>
                        <br></br>
                        <div> { loading ? (
                <BeatLoading />
            ) : (<>
                        <p className='fs-16 m-0'>Total Revenue</p>
                        <h1 style={{ color: '#3498DB' }}>${formatNumberWithCommas(metrics?.totalRevenue)}</h1>
                        <br></br>
                        <p className='fs-16 m-0'>Media Cost</p>
                        <h1 style={{ color: '#E74C3C' }}>${formatNumberWithCommas(metrics?.mediaCost)}</h1>
                        <br></br>
                        <p className='fs-16'>Omega Cost</p>
                        <h1 style={{ color: '#F1C40F' }}>${formatNumberWithCommas(metrics?.omegaCost)}</h1>
                        <br></br>
                        <p className='fs-16'>Net Income</p>
                        <h1 style={{ color: '#17B26A' }}>${formatNumberWithCommas(metrics?.netIncome)}</h1>
                        </>
                        )}
                        </div>
                    </Grid>
                </Grid>
            </Box>
        </div>
    )
}
